import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { NAV, slug } from "@/data/nav";
import { Logo } from "./Logo";
import { CtaButton, BookMeeting } from "./Cta";
import { MegaPanel } from "./MegaPanel";
import { MobileMenu } from "./MobileMenu";
import { LangToggle } from "./LangToggle";
import { comingSoon } from "@/lib/site";

const base = "relative flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-2 text-[12.5px] font-medium transition-colors duration-300 xl:px-2 xl:text-[13.5px] 2xl:px-3";

const TopItem = ({ item, open, setOpen }) => {
  const id = `nav-link-${slug(item.short || item.label)}`;
  if (item.items) {
    const active = open === item.id;
    return (
      <button
        type="button"
        data-testid={id}
        onMouseEnter={() => setOpen(item.id)}
        onClick={() => setOpen(active ? null : item.id)}
        className={`${base} ${active ? "text-white" : "text-slate-300 hover:text-white"}`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${active ? "rotate-180 text-violet-300" : ""}`} />
      </button>
    );
  }
  if (item.to) {
    return (
      <NavLink to={item.to} end data-testid={id} onMouseEnter={() => setOpen(null)}
        className={({ isActive }) => `${base} ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`}>
        {item.short || item.label}
      </NavLink>
    );
  }
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={id} onMouseEnter={() => setOpen(null)} className={`${base} text-slate-300 hover:text-white`} title={item.label}>
        {item.short || item.label}
        <ArrowUpRight className="h-3.5 w-3.5 text-violet-300" />
      </a>
    );
  }
  return (
    <button type="button" data-testid={id} onMouseEnter={() => setOpen(null)}
      onClick={() => comingSoon(item.label)}
      className={`${base} text-slate-300 hover:text-white`} title={item.label}>
      {item.short || item.label}
      {item.external && <ArrowUpRight className="h-3.5 w-3.5 text-violet-300" />}
    </button>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(null); setMobile(false); }, [pathname]);

  const solid = scrolled || open || mobile;
  const group = NAV.find((n) => n.id === open);

  return (
    <>
    <header data-testid="site-header" onMouseLeave={() => setOpen(null)}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${solid ? "border-white/10 bg-[#0B0716]/85 backdrop-blur-xl" : "border-transparent bg-transparent"}`}>
      <div className="wrap flex h-[72px] items-center justify-between">
        <Logo />
        <nav className="hidden items-center lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => <TopItem key={item.label} item={item} open={open} setOpen={setOpen} />)}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <BookMeeting size="sm" testid="header-book-meeting-cta" className="hidden whitespace-nowrap min-[1360px]:inline-flex" />
          <button type="button" data-testid="mobile-menu-button" aria-label="Menu" onClick={() => setMobile((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white lg:hidden">
            {mobile ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {group && (
          <motion.div key="mega" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="hidden lg:block">
            <MegaPanel group={group} />
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    <MobileMenu open={mobile} />
    </>
  );
};
